import { inject, Injectable, signal } from '@angular/core';
import { HttpEvent, HttpEventType, HttpResponse } from '@angular/common/http';
import { firstValueFrom, Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { HypervisorType } from '../enums';
import {
  DEFAULT_PROVISIONING_ESTIMATE_SECONDS,
  ProvisioningJobCreateRequest,
  ProvisioningJobResponse,
  VmProvisionDownloadState,
  VmProvisionRequest,
} from '../models';
import { AuthStore } from '../stores';
import { VmProvisionApiService } from './vm-provision-api.service';

@Injectable({ providedIn: 'root' })
export class VmProvisionService {
  private readonly apiService = inject(VmProvisionApiService);
  private readonly authStore = inject(AuthStore);
  private readonly toastrService = inject(ToastrService);
  private readonly translateService = inject(TranslateService);

  private activeSubscription: Subscription | null = null;
  private activeAbortController: AbortController | null = null;
  private unsubscribeProgress: (() => void) | null = null;
  private elapsedTimer: ReturnType<typeof setInterval> | null = null;

  private readonly _state = signal<VmProvisionDownloadState>(this.createInitialState());
  readonly $state = this._state.asReadonly();

  private createInitialState(): VmProvisionDownloadState {
    return {
      status: 'idle',
      progress: 0,
      downloadedBytes: 0,
      totalBytes: 0,
      totalBytesKnown: false,
      speedBytesPerSec: 0,
      timeRemainingSeconds: null,
      estimatedRemainingSeconds: null,
      elapsedSeconds: 0,
      logs: [],
    };
  }

  async startJob(
    request: ProvisioningJobCreateRequest,
    customerId?: string | number | null,
  ): Promise<ProvisioningJobResponse> {
    return firstValueFrom(this.apiService.startJob(request, customerId));
  }

  async getJobStatus(jobId: string, customerId?: string | number | null): Promise<ProvisioningJobResponse> {
    return firstValueFrom(this.apiService.getJobStatus(jobId, customerId));
  }

  startProvisioning(request: VmProvisionRequest): void {
    this.cancel();

    const hypervisor = HypervisorType.toHypervisor(request.hypervisor);
    const defaultFileName = this.buildDefaultFileName(request, hypervisor);

    this.initProvisioningState(hypervisor, defaultFileName);
    this.startElapsedTimer();

    const createReq: ProvisioningJobCreateRequest = {
      hypervisor,
      name: request.name?.trim() || undefined,
    };

    this.apiService.startJob(createReq, request.customerId).subscribe({
      next: (jobResponse) => this.handleJobStartSuccess(jobResponse, defaultFileName, request.customerId),
      error: (err) => this.handleJobStartError(err),
    });
  }

  private buildDefaultFileName(request: VmProvisionRequest, hypervisor: string | null): string {
    const option = HypervisorType.getOption(request.hypervisor);
    const fileExt = option?.fileExtension ?? '.zip';
    const cleanName = request.name?.trim() ? request.name.trim().toLowerCase().replace(/\s+/g, '-') : 'defenderbox';
    const hypName = hypervisor ? hypervisor.toLowerCase() : 'vm';
    return `${cleanName}-${hypName}${fileExt}`;
  }

  private initProvisioningState(hypervisor: any, defaultFileName: string): void {
    this._state.set({
      status: 'provisioning',
      currentStep: 'PREPARING',
      stepLabel: 'boxes.provisioning.phases.preparing',
      hypervisor,
      progress: 0,
      downloadedBytes: 0,
      totalBytes: 0,
      totalBytesKnown: false,
      speedBytesPerSec: 0,
      timeRemainingSeconds: null,
      estimatedRemainingSeconds: DEFAULT_PROVISIONING_ESTIMATE_SECONDS,
      elapsedSeconds: 0,
      fileName: defaultFileName,
      logs: [],
    });
  }

  private handleJobStartSuccess(
    jobResponse: ProvisioningJobResponse,
    defaultFileName: string,
    customerId?: string | number | null,
  ): void {
    const jobId = jobResponse.jobId;
    this.applyJobResponseToState(jobResponse);

    if (jobResponse.status === 'COMPLETED') {
      const baseUrl = this.apiService.buildCustomerBaseUrl(customerId);
      const downloadUrl = jobResponse.downloadUrl || `${baseUrl}/jobs/${jobId}/download`;
      this.handleJobCompleted(downloadUrl, defaultFileName);
      return;
    }

    this.startProgressSubscription(jobId, defaultFileName, customerId);
  }

  private applyJobResponseToState(jobResponse: ProvisioningJobResponse): void {
    this._state.update((s) => ({
      ...s,
      jobId: jobResponse.jobId,
      status: jobResponse.status === 'COMPLETED' ? 'downloading' : 'provisioning',
      currentStep: jobResponse.currentStep || 'PREPARING',
      hypervisor: jobResponse.hypervisor || s.hypervisor,
      progress: jobResponse.progressPercentage ?? 0,
      estimatedRemainingSeconds: jobResponse.estimatedRemainingSeconds ?? s.estimatedRemainingSeconds,
    }));
  }

  private startProgressSubscription(jobId: string, defaultFileName: string, customerId?: string | number | null): void {
    const freshToken = this.authStore.accessToken() ?? undefined;
    this.unsubscribeProgress = this.subscribeProgress(
      jobId,
      (payload) => this.onProgressPayload(payload),
      (downloadUrl) => this.handleJobCompleted(downloadUrl, defaultFileName),
      (errorMessage) => this.onProgressError(errorMessage),
      customerId,
      freshToken,
    );
  }

  private onProgressPayload(payload: ProvisioningJobResponse): void {
    this._state.update((s) => ({
      ...s,
      currentStep: payload.currentStep,
      hypervisor: payload.hypervisor || s.hypervisor,
      progress: Math.min(Math.max(payload.progressPercentage ?? s.progress, 0), 100),
      estimatedRemainingSeconds: payload.estimatedRemainingSeconds,
    }));
  }

  private onProgressError(errorMessage: string): void {
    this.clearElapsedTimer();
    this.toastrService.error(errorMessage);
    this._state.update((s) => ({
      ...s,
      status: 'error',
      stepLabel: 'boxes.provisioning.status.error',
      error: errorMessage,
    }));
  }

  private handleJobStartError(err: any): void {
    this.clearElapsedTimer();
    const errorMsg =
      err?.error?.message || err?.message || this.translateService.instant('boxes.provisioning.errors.startFailed');
    this.toastrService.error(errorMsg);
    this._state.update((s) => ({
      ...s,
      status: 'error',
      stepLabel: 'boxes.provisioning.status.error',
      error: errorMsg,
    }));
  }

  subscribeProgress(
    jobId: string,
    onUpdate: (payload: ProvisioningJobResponse) => void,
    onComplete: (downloadUrl: string) => void,
    onError: (errorMessage: string) => void,
    customerId?: string | number | null,
    token?: string | null,
  ): () => void {
    const baseUrl = this.apiService.buildCustomerBaseUrl(customerId);
    const authToken = token || this.authStore.accessToken();
    const abortController = new AbortController();
    this.activeAbortController = abortController;

    const stateTracker = { isClosed: false, pollInterval: null as ReturnType<typeof setInterval> | null };
    const cleanup = () => this.cleanupSubscription(stateTracker, abortController);

    const processPayload = (payload: ProvisioningJobResponse) =>
      this.handleReceivedPayload(payload, stateTracker, cleanup, onUpdate, onComplete, onError, baseUrl, jobId);

    this.initProgressStream(baseUrl, jobId, authToken, abortController, stateTracker, processPayload, customerId);

    return cleanup;
  }

  private cleanupSubscription(
    stateTracker: { isClosed: boolean; pollInterval: ReturnType<typeof setInterval> | null },
    abortController: AbortController,
  ): void {
    stateTracker.isClosed = true;
    if (stateTracker.pollInterval) {
      clearInterval(stateTracker.pollInterval);
      stateTracker.pollInterval = null;
    }
    abortController.abort();
  }

  private handleReceivedPayload(
    payload: ProvisioningJobResponse,
    stateTracker: { isClosed: boolean },
    cleanup: () => void,
    onUpdate: (payload: ProvisioningJobResponse) => void,
    onComplete: (downloadUrl: string) => void,
    onError: (errorMessage: string) => void,
    baseUrl: string,
    jobId: string,
  ): void {
    if (stateTracker.isClosed) return;
    onUpdate(payload);

    if (payload.status === 'COMPLETED') {
      cleanup();
      const downloadUrl = payload.downloadUrl || `${baseUrl}/jobs/${jobId}/download`;
      onComplete(downloadUrl);
    } else if (payload.status === 'FAILED') {
      cleanup();
      onError(payload.error || this.translateService.instant('provisioning.status.FAILED'));
    }
  }

  private async initProgressStream(
    baseUrl: string,
    jobId: string,
    authToken: string | null | undefined,
    abortController: AbortController,
    stateTracker: { isClosed: boolean; pollInterval: ReturnType<typeof setInterval> | null },
    processPayload: (payload: ProvisioningJobResponse) => void,
    customerId?: string | number | null,
  ): Promise<void> {
    try {
      const response = await this.apiService.fetchProgressStream(baseUrl, jobId, authToken, abortController.signal);
      if (!response.ok || !response.body) {
        this.startPollingFallback(jobId, customerId, abortController, stateTracker, processPayload);
        return;
      }
      await this.readEventStream(response.body, stateTracker, processPayload);
    } catch {
      this.startPollingFallback(jobId, customerId, abortController, stateTracker, processPayload);
    }
  }

  private async readEventStream(
    body: ReadableStream<Uint8Array>,
    stateTracker: { isClosed: boolean },
    processPayload: (payload: ProvisioningJobResponse) => void,
  ): Promise<void> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (!stateTracker.isClosed) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split(/\r\n|\r|\n/);
      buffer = lines.pop() || '';

      for (const line of lines) {
        this.processStreamLine(line, processPayload);
      }
    }
  }

  private processStreamLine(line: string, processPayload: (payload: ProvisioningJobResponse) => void): void {
    const trimmed = line.trim();
    if (!trimmed.startsWith('data:')) return;
    const dataStr = trimmed.substring(5).trim();
    if (!dataStr) return;

    try {
      const payload: ProvisioningJobResponse = JSON.parse(dataStr);
      processPayload(payload);
    } catch {
      // Ignore malformed individual frame
    }
  }

  private startPollingFallback(
    jobId: string,
    customerId: string | number | null | undefined,
    abortController: AbortController,
    stateTracker: { isClosed: boolean; pollInterval: ReturnType<typeof setInterval> | null },
    processPayload: (payload: ProvisioningJobResponse) => void,
  ): void {
    if (stateTracker.isClosed || abortController.signal.aborted) {
      return;
    }

    const pollJobStatus = async () => {
      if (stateTracker.isClosed) return;
      try {
        const payload = await firstValueFrom(this.apiService.getJobStatus(jobId, customerId));
        processPayload(payload);
      } catch {
        // Handled silently during polling interval
      }
    };

    pollJobStatus();
    stateTracker.pollInterval = setInterval(pollJobStatus, 1500);
  }

  private handleJobCompleted(downloadUrl: string, defaultFileName: string): void {
    const downloadStartTime = Date.now();
    this.initDownloadState(downloadUrl);

    this.activeSubscription = this.apiService.downloadImage(downloadUrl).subscribe({
      next: (event: HttpEvent<Blob>) => {
        if (event.type === HttpEventType.DownloadProgress) {
          this.handleDownloadProgress(event, downloadStartTime);
        } else if (event.type === HttpEventType.Response) {
          this.handleDownloadResponse(event as HttpResponse<Blob>, defaultFileName, downloadUrl);
        }
      },
      error: (err) => this.handleDownloadError(err),
    });
  }

  private initDownloadState(downloadUrl: string): void {
    this._state.update((s) => ({
      ...s,
      status: 'downloading',
      progress: 0,
      currentStep: 'COMPLETED',
      stepLabel: 'boxes.provisioning.downloadingMessage',
      downloadUrl,
      downloadedBytes: 0,
      totalBytes: 0,
      totalBytesKnown: false,
      speedBytesPerSec: 0,
      timeRemainingSeconds: null,
    }));
  }

  private handleDownloadProgress(event: any, downloadStartTime: number): void {
    const now = Date.now();
    const downloaded = event.loaded;
    const total = event.total ?? 0;
    const totalKnown = event.total !== undefined && event.total > 0;
    const percent = totalKnown ? Math.min(Math.round((downloaded / total) * 100), 100) : 0;

    const { speedBytesPerSec, timeRemainingSeconds } = this.calculateSpeedAndEta(
      downloaded,
      total,
      totalKnown,
      (now - downloadStartTime) / 1000,
    );

    this._state.update((s) => ({
      ...s,
      status: 'downloading',
      progress: percent,
      downloadedBytes: downloaded,
      totalBytes: total,
      totalBytesKnown: totalKnown,
      speedBytesPerSec,
      timeRemainingSeconds,
      stepLabel: 'boxes.provisioning.downloadingMessage',
    }));
  }

  private calculateSpeedAndEta(
    downloaded: number,
    total: number,
    totalKnown: boolean,
    elapsedSec: number,
  ): { speedBytesPerSec: number; timeRemainingSeconds: number | null } {
    let speedBytesPerSec = 0;
    let timeRemainingSeconds: number | null = null;

    if (elapsedSec > 0.2 && downloaded > 0) {
      speedBytesPerSec = Math.round(downloaded / elapsedSec);
      if (totalKnown && speedBytesPerSec > 0) {
        const remainingBytes = Math.max(total - downloaded, 0);
        timeRemainingSeconds = Math.max(Math.round(remainingBytes / speedBytesPerSec), 0);
      }
    }

    return { speedBytesPerSec, timeRemainingSeconds };
  }

  private handleDownloadResponse(response: HttpResponse<Blob>, defaultFileName: string, downloadUrl: string): void {
    this.clearElapsedTimer();
    const blob = response.body ?? new Blob([]);
    const resolvedFileName =
      this.apiService.parseFileName(response.headers.get('content-disposition')) || defaultFileName;
    const checksum = this.extractDigestHeader(response);

    const blobUrl = window.URL.createObjectURL(blob);
    this.triggerBrowserDownload(blobUrl, resolvedFileName);
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);

    this.completeDownloadState(blob, blobUrl, downloadUrl, resolvedFileName, checksum);
  }

  private extractDigestHeader(response: HttpResponse<Blob>): string | null {
    const header =
      response.headers.get('content-digest') ||
      response.headers.get('digest') ||
      response.headers.get('x-checksum-sha256');
    return this.apiService.parseSha256Digest(header);
  }

  private completeDownloadState(
    blob: Blob,
    blobUrl: string,
    downloadUrl: string,
    fileName: string,
    checksum: string | null,
  ): void {
    this._state.set({
      status: 'completed',
      currentStep: 'COMPLETED',
      stepLabel: 'boxes.provisioning.status.completed',
      progress: 100,
      downloadedBytes: blob.size,
      totalBytes: blob.size,
      totalBytesKnown: true,
      speedBytesPerSec: 0,
      timeRemainingSeconds: 0,
      estimatedRemainingSeconds: 0,
      elapsedSeconds: this._state().elapsedSeconds,
      checksum: checksum ?? undefined,
      fileName,
      blob,
      blobUrl,
      downloadUrl,
      logs: [],
    });
  }

  private handleDownloadError(err: any): void {
    this.clearElapsedTimer();
    const errorMessage =
      err.status === 401
        ? this.translateService.instant('boxes.provisioning.errors.unauthorized')
        : err?.error?.message ||
          err?.message ||
          this.translateService.instant('boxes.provisioning.errors.downloadFailed');

    this.toastrService.error(errorMessage);
    this._state.update((s) => ({
      ...s,
      status: 'error',
      stepLabel: 'boxes.provisioning.status.error',
      error: errorMessage,
    }));
  }

  cancel(): void {
    this.clearElapsedTimer();
    if (this.activeSubscription) {
      this.activeSubscription.unsubscribe();
      this.activeSubscription = null;
    }
    if (this.unsubscribeProgress) {
      this.unsubscribeProgress();
      this.unsubscribeProgress = null;
    }
    if (this.activeAbortController) {
      this.activeAbortController.abort();
      this.activeAbortController = null;
    }
    const currentBlobUrl = this._state().blobUrl;
    if (currentBlobUrl) {
      window.URL.revokeObjectURL(currentBlobUrl);
    }
    this._state.set(this.createInitialState());
  }

  private startElapsedTimer(): void {
    const startTime = Date.now();
    this.elapsedTimer = setInterval(() => {
      this._state.update((s) => {
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        const est =
          s.estimatedRemainingSeconds !== null && s.estimatedRemainingSeconds !== undefined
            ? Math.max(s.estimatedRemainingSeconds - 1, 0)
            : Math.max(DEFAULT_PROVISIONING_ESTIMATE_SECONDS - elapsed, 0);
        return {
          ...s,
          elapsedSeconds: elapsed,
          estimatedRemainingSeconds: est,
        };
      });
    }, 1000);
  }

  private clearElapsedTimer(): void {
    if (this.elapsedTimer !== null) {
      clearInterval(this.elapsedTimer);
      this.elapsedTimer = null;
    }
  }

  triggerBrowserDownload(blobUrl: string, fileName: string): void {
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  async copyToClipboard(text: string): Promise<boolean> {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
      return this.fallbackCopyToClipboard(text);
    } catch {
      return false;
    }
  }

  private fallbackCopyToClipboard(text: string): boolean {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const success = document.execCommand('copy');
    document.body.removeChild(textarea);
    return success;
  }

  parseSha256Digest(headerValue: string | null | undefined): string | null {
    return this.apiService.parseSha256Digest(headerValue);
  }

  parseFileName(contentDisposition: string | null | undefined): string | null {
    return this.apiService.parseFileName(contentDisposition);
  }
}
